import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import { loadAuth } from "../../helpers/loadAuth";
import { logout as performLogout } from "../../helpers/authSession";

const ROLE_VA_DISTRIBUTOR = "va-distributor";
const ROLE_AUDIOLOGIST = "audiologist";

const AuthContext = createContext({
  user: null,
  roles: [],
  permissions: [],
  canSoftDelete: false,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  isAdmin: () => false,
});

export function AuthProvider({ children }) {
  const hasToken = !!localStorage.getItem("authToken");
  const [auth, setAuth] = useState(null);
  const [isLoading, setIsLoading] = useState(hasToken);

  const logout = useCallback(() => {
    // Every role returns to the shared login page after Auth0 logout.
    performLogout("/login");
  }, []);

  useEffect(() => {
    if (!hasToken) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    loadAuth().then((data) => {
      if (cancelled) return;
      if (data) {
        setAuth(data);
      }
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [hasToken]);

  const user = auth
    ? {
        id: auth.id,
        email: auth.email,
        name: auth.name,
        picture: auth.picture,
      }
    : null;
  const roles = auth?.roles ?? [];
  const permissions = auth?.permissions ?? [];
  const vaAccessDenied = Boolean(auth?.vaAccessDenied);
  const normalizedEmail = String(auth?.email || user?.email || "")
    .trim()
    .toLowerCase();
  const allowList = Array.isArray(auth?.softDeleteAllowedEmails)
    ? auth.softDeleteAllowedEmails.map((e) => String(e || "").trim().toLowerCase())
    : [];
  // Server flag, or allow-list match (covers stale JWT / missing flag).
  const canSoftDelete =
    Boolean(auth?.canSoftDelete) ||
    (normalizedEmail.length > 0 && allowList.includes(normalizedEmail));

  const value = useMemo(() => {
    const hasRole = (role) => roles.includes(role);
    const hasAnyRole = (roleList) =>
      roleList.some((role) => roles.includes(role));

    return {
      user,
      roles,
      permissions,
      vaAccessDenied,
      canSoftDelete,
      isLoading,
      logout,
      hasRole,
      hasAnyRole,
      hasPermission: (key) => permissions.includes(key),
      hasAnyPermission: (...keys) =>
        keys.some((k) => permissions.includes(k)),
      isAdmin: () => roles.includes("admin"),
      isVaDistributor: () => hasRole(ROLE_VA_DISTRIBUTOR),
      isAudiologist: () => hasRole(ROLE_AUDIOLOGIST),
    };
  }, [user, roles, permissions, vaAccessDenied, canSoftDelete, isLoading, logout]);

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
